import { gsap } from 'gsap'

import { deepMerge } from 'js/utils/Utils'
import { lerp, roundEx } from 'js/utils/Maths'
import Common from 'js/_core/Common'

const defaults = {
  instanceName: 'teamsNavCurrentUI',
  $names: {
    navList: '[data-teams-nav-list]',
    navItem: '[data-teams-nav-item]',
    navBar: '[data-teams-nav-bar]',
  },
  classNames: {
    current: 'is-current',
  },
  effectInfo: {
    lerpFactor: 0.12,
    opacity: {
      default: 1, // カレント or 非ホバー時
      hover: 0.4, // ホバー時
    },
  },
}

export default class TeamsNavCurrentUI {
  $contentRoot = null
  $navList = null
  $navItems = []
  $navBar = null

  navInfo = []
  barInfo = {
    scaleX: 0,
    scaleXLerp: 0,
    translateX: 0,
    translateXLerp: 0,
  }

  isPageTransition = false

  constructor(options = {}) {
    deepMerge(this, defaults, options)
  }

  init() {
    this._set$()
    this.createInfo()
    this._setInfo()
    this._addEventListeners()
    this._addHandlers()
  }

  destroy() {
    this.isPageTransition = true
    this._removeEventListeners()
    this._removeHandlers()
  }

  _set$() {
    this.$navList = this.$contentRoot.querySelector(this.$names.navList)
    this.$navItems = this.$contentRoot.querySelectorAll(this.$names.navItem)
    this.$navBar = this.$contentRoot.querySelector(this.$names.navBar)
  }

  createInfo() {
    this.navInfo = []
    this.$navItems.forEach(($item, index) => {
      $item.dataset['navIndex'] = index

      const info = {
        $item,
        index,
        left: $item.offsetLeft,
        width: $item.getBoundingClientRect().width,
        isCurrent: $item.classList.contains(this.classNames.current),
        isHover: false,
      }
      this.navInfo.push(info)
    })
  }

  _addHandlers() {
    if (Common.raf) {
      Common.raf.addHandler(() => {
        this.onRaf()
      }, this.instanceName)
    }

    if (Common.resize) {
      Common.resize.addHandler(() => {
        this.onResize()
      }, this.instanceName)
    }
  }

  _removeHandlers() {
    if (Common.raf) {
      Common.raf.removeHandler(this.instanceName)
    }

    if (Common.resize) {
      Common.resize.removeHandler(this.instanceName)
    }
  }

  _addEventListeners() {
    this.$navItems.forEach(($item) => {
      $item.addEventListener('mouseenter', this.onItemMouseEnter)

      const $a = $item.querySelector('a')
      if ($a) {
        $a.addEventListener('click', () => {
          this.isPageTransition = true
        })
      }

      // $item.addEventListener('mouseleave', this.onItemMouseLeave)
    })

    this.$navList.addEventListener('mouseleave', this.onItemMouseLeave)
  }

  _removeEventListeners() {
    this.$navItems.forEach(($item) => {
      $item.removeEventListener('mouseenter', this.onItemMouseEnter)
      // $item.removeEventListener('mouseleave', this.onItemMouseLeave)
    })

    this.$navList.removeEventListener('mouseleave', this.onItemMouseLeave)
  }

  onItemMouseEnter = (e) => {
    const $item = e.currentTarget
    let index = $item.dataset['navIndex']
    index = index ? parseInt(index) : undefined
    if (index === undefined) return
    const info = this.navInfo[index]
    if (!info) return

    this.navInfo.forEach((info, j) => {
      info.isHover = j === index
    })

    // === opacity変更 ===
    if (this.$navBar) {
      requestAnimationFrame(() => {
        gsap.killTweensOf(this.$navBar, 'opacity')
        gsap.to(this.$navBar, {
          opacity: this.effectInfo.opacity.hover,
          duration: 0.3,
          ease: 'none',
          overwrite: 'auto',
        })
      })
    }
  }

  onItemMouseLeave = () => {
    this.navInfo.forEach((info) => {
      info.isHover = false
    })

    // === opacityを戻す ===
    if (this.$navBar) {
      requestAnimationFrame(() => {
        gsap.killTweensOf(this.$navBar, 'opacity')
        gsap.to(this.$navBar, {
          opacity: this.effectInfo.opacity.default,
          duration: 0.5,
          ease: 'none',
          overwrite: 'auto',
        })
      })
    }
  }

  _setInfo() {
    this.navInfo.forEach((info) => {
      info.left = info.$item.offsetLeft
      info.width = info.$item.getBoundingClientRect().width
    })
  }

  updateStyle() {
    if (this.isPageTransition) return

    const hoverInfo = this.navInfo.find((info) => info.isHover)
    const currentInfo = this.navInfo.find((info) => info.isCurrent)

    if (hoverInfo) {
      this.barInfo.translateX = hoverInfo.left
      this.barInfo.scaleX = hoverInfo.width
    } else if (currentInfo) {
      this.barInfo.translateX = currentInfo.left
      this.barInfo.scaleX = currentInfo.width
    }

    this.barInfo.translateXLerp = lerp(this.barInfo.translateXLerp, this.barInfo.translateX, this.effectInfo.lerpFactor / Common.raf.fpsMultiplier)

    this.barInfo.scaleXLerp = lerp(this.barInfo.scaleXLerp, this.barInfo.scaleX, this.effectInfo.lerpFactor / Common.raf.fpsMultiplier)

    this.barInfo.translateXLerp = roundEx(this.barInfo.translateXLerp, 1000)
    this.barInfo.scaleXLerp = roundEx(this.barInfo.scaleXLerp, 1000)

    if (this.$navBar) {
      this.$navBar.style.transform = `translateX(${this.barInfo.translateXLerp}px) scaleX(${this.barInfo.scaleXLerp})`
    }
  }

  onResize() {
    this._setInfo()
  }

  onRaf() {
    this.updateStyle()
  }
}